import {NxButton, NxGenericTextField, NxLoader} from '@nextbank/ui-components';
import {useFormikContext} from 'formik';
import startCase from 'lodash/startCase';
import React, {ReactElement, useContext, useEffect, useState} from 'react';
import {Trans} from 'react-i18next';
import {useParams} from 'react-router';
import Base64PdfViewer from './Base64PdfViewer';
import {CREDIT_BUREAU_CREDIT_CHECK_URL, CREDIT_BUREAU_URL, PRINTS_URL} from '../../../../../../constants/api-urls';
import {UrlParams} from '../../../../../../routes/routes.model';
import useGet from '../../../../../../shared/hooks/use-get.hook';
import usePost from '../../../../../../shared/hooks/use-post.hook';
import useSingleQuery from '../../../../../../shared/hooks/use-single-query.hook';
import {
  CreditBureauProviderType,
  CreditBureauSearchCriteria,
  CreditBureauStatusType,
  CreditScore
} from '../../../../../../shared/model/credit-bureau-provider.model';
import {ConfiguredField} from '../../../../../../shared/model/field.model';
import {FieldError} from '../../../../../../shared/model/phase-validation-result.model';
import {AppSnackbarContext} from '../../../../../shared/app-snackbar-provider/AppSnackbarProvider';
import FormGroup from '../../../../../shared/form-column/FormGroup';
import {StepContext} from '../loan-application-step/LoanApplicationStep';
import styles from './ConfiguredFieldCreditCheck.module.scss';
import {ConfiguredFieldWrapper} from './ConfiguredFieldWrapper';
import {FieldDisablementReason} from './field-disablement-reason.model';

interface Props {
  field: ConfiguredField;
  label: React.ReactNode;
  name: string;
  disablementReason?: FieldDisablementReason;
}

export default function ConfiguredFieldCreditCheck(
  {field, label, name, disablementReason}: Props
): React.ReactElement {
  const {applicationId} = useParams<UrlParams>();
  const { setFieldValue } = useFormikContext();
  const defaultProvider = CreditBureauProviderType.CIBI;
  const isCreditBureauEnabled = useSingleQuery<boolean>(`${CREDIT_BUREAU_URL}/${defaultProvider}`);
  const getCreditCheckByApplicationId = useGet<CreditScore>(`${CREDIT_BUREAU_URL}/${defaultProvider}/credit-check/`);
  const postCreditCheck = usePost<CreditScore, CreditBureauSearchCriteria>(CREDIT_BUREAU_CREDIT_CHECK_URL);
  const {showErrorMessage, showSuccessMessage} = useContext(AppSnackbarContext);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isViewingPDF, setIsViewingPDF] = useState<boolean>(false);
  const [creditScore, setCreditScore] = useState<CreditScore | null>(null);
  const {isStepReadonly} = useContext(StepContext);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    try{
      fetchCreditCheck({provider: defaultProvider, applicationId:applicationId, includeCreditReport: false});
    } catch (error) {
      showErrorMessage(error.message);
    } finally {
      setIsSubmitting(false)
    }
    }, [isCreditBureauEnabled, applicationId]);

  const fetchCreditCheck = async (urlParams : CreditBureauSearchCriteria) => {
    if (isCreditBureauEnabled && applicationId) {
      const params = new URLSearchParams();

      if (urlParams) {
        Object.keys(urlParams).forEach(key => {
          params.append(key, String(urlParams[key]));
        });
      }

      const response = await getCreditCheckByApplicationId(params);
      setCreditScore(response);
      setFieldValue(`${name}.value`, response?.id);
    }
  };

  const viewCreditCheck = async () => {
    try {
      setIsViewingPDF(true);
      await fetchCreditCheck({provider: defaultProvider, applicationId:applicationId, includeCreditReport: true}).then(response => {
        setIsPdfDialogOpen(true);
        setIsViewingPDF(false);
      })
    } catch (error) {
      showErrorMessage(error.message);
      setIsViewingPDF(false);
    }
  };

  const closePdfDialog = () => {
    setIsPdfDialogOpen(false);
  };

  const submitCreditCheck = async (): Promise<void> => {
    setIsSubmitting(true);
    postCreditCheck({provider: defaultProvider, applicationId: applicationId})
      .then(response => {
        if(response.status === CreditBureauStatusType.ERROR){
          showErrorMessage(createFieldErrors(response.errors));
          return;
        }
        setCreditScore(response);
        setFieldValue(`${name}.value`, response?.id);
        showSuccessMessage('Credit Check is successful');
      })
      .catch(error => showErrorMessage(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const createFieldErrors = (errors: FieldError[]): ReactElement => (
    <>
      {errors && errors.map((err, index) => (
        <div key={err.fieldPath === '' || err.fieldPath === null ? index : err.fieldPath}>
          <span>{`${err.fieldName}:`}</span> {Array.isArray(err.messages) ? err.messages.join(', ') : err.messages}
        </div>
      ))}
    </>
  );

  const renderPdfDialog = () => {
    if (!creditScore?.id || creditScore.reportPdf == null) return null;

    return (
      <Base64PdfViewer
        isOpen={isPdfDialogOpen}
        onClose={closePdfDialog}
        base64Pdf={creditScore.reportPdf}
        title='credit-check-pdf-viewer'
      />
    );
  };

  return !isCreditBureauEnabled
    ? <></>
    : (
      <>
        <FormGroup className={styles.inputs}>
          {isSubmitting && (
            <div className={styles.loaderOverlay}>
              <NxLoader information={'Waiting for credit check provider response'} />
            </div>
          )}
          <div className={styles.fieldsContainer}>
            <ConfiguredFieldWrapper enabled={field.enabled} disablementReason={disablementReason}>
            <NxGenericTextField
              disabled
              name={'score'}
              label={label}
              value={creditScore?.status === CreditBureauStatusType.MATCH
                ? creditScore?.score
                : startCase(creditScore?.status.toLowerCase())
                ?? ''}
            />
            </ConfiguredFieldWrapper>

            <NxGenericTextField
              disabled
              name={'riskGrade'}
              label={'Risk Grade'}
              value={creditScore?.riskGrade ? startCase(creditScore.riskGrade.toLowerCase()) : ''}
            />
          </div>

          <div className={styles.buttonContainer}>
            <NxButton
              onClick={submitCreditCheck}
              className={styles.button}
              disabled={isSubmitting || isStepReadonly || !!disablementReason}>
              <Trans>LOAN_CONFIGURATIONS.CREDIT_CHECK</Trans>
            </NxButton>

            <NxButton
              onClick={viewCreditCheck}
              className={styles.button}
              disabled={!creditScore?.id || isViewingPDF}>
              <Trans>LOAN_CONFIGURATIONS.CREDIT_CHECK_VIEW</Trans>
            </NxButton>
          </div>
        </FormGroup>
        {renderPdfDialog()}
      </>
    );
}
